import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { $createReceiptTransactionStore, $deleteReceiptStore, $updateTransactionStore } from '@lib/store';
import { AmountUtils, cn, ColorUtils, DateUtils, FormatUtils } from '@shared/utils';
import { type TransactionResponse } from '@shared/api/rest/transactions';
import { Icon, Table, TableCellType } from '@shared/widgets';
import { AddCategoryModal, AddInReports, AddNoteModal, AddReceiptModal, ShowInReports } from './subPages';
import EditIcon from '@assets/icons/edit.svg';
import FileIcon from '@assets/icons/file.svg';

interface IProps {
  isLoading?: boolean;
  columns: string[];
  items: TransactionResponse[];
  currentPage: number;
  totalItemCount: number;
  countItemPage: number;
  onChangePage: (pageNumber: number) => void;
  onChangeCountItemPage: (pageNumber: number) => void;
  onUpdate?: () => void;
}

export const TransactionTable = (props: IProps) => {
  const [currentTransactionItem, setCurrentTransactionItem] = useState<TransactionResponse | undefined>();
  const [showNoteModal, setShowNoteModal] = useState<boolean>(false);
  const [showReceiptModal, setShowReceiptModal] = useState<boolean>(false);
  const [showCategoryModal, setShowCategoryModal] = useState<boolean>(false);

  const { t } = useTranslation('reports');

  const addReceipt = async (receipt: FormData) => {
    if (!currentTransactionItem) return;
    await $createReceiptTransactionStore.events.create({ refNum: currentTransactionItem.ref, receipt: receipt });
    setShowReceiptModal(false);
    props.onUpdate?.();
  };

  const openShowNoteModal = (item: TransactionResponse) => {
    setCurrentTransactionItem(item);
    setShowNoteModal(true);
  };
  const openShowReceiptModal = (item: TransactionResponse) => {
    setCurrentTransactionItem(item);
    setShowReceiptModal(true);
  };
  const openShowCategoryModal = (item: TransactionResponse) => {
    setCurrentTransactionItem(item);
    setShowCategoryModal(true);
  };

  const removeReceipt = async () => {
    if (!currentTransactionItem) return;
    await $deleteReceiptStore.events.remove(currentTransactionItem.ref);
  };

  const updateNote = async (note: string) => {
    if (!currentTransactionItem) return;
    await $updateTransactionStore.events.update({
      note: note,
      refNum: currentTransactionItem.ref,
      category: currentTransactionItem.category ? currentTransactionItem.category : undefined,
    });
    setShowNoteModal(false);
    props.onUpdate?.();
  };

  const updateCategory = async (category: string) => {
    if (!currentTransactionItem) return;
    await $updateTransactionStore.events.update({ refNum: currentTransactionItem.ref, category: category });
    setShowCategoryModal(false);
    props.onUpdate?.();
  };

  const cells: Record<
    string,
    (row: TransactionResponse) => {
      type: TableCellType;
      value: React.ReactNode;
    }
  > = useMemo(
    () => ({
      Status: (row: TransactionResponse) => {
        const color = ColorUtils.getTransactionStatusStyles(row.status);
        return {
          type: TableCellType.Custom,
          value: (
            <div className={cn('whitespace-nowrap px-2 py-1 text-center font-normal', color.bg, color.text)}>
              {t(`statuses.cardTransaction.${row.status}`, { ns: 'common' })}
            </div>
          ),
        };
      },
      Card: (row: TransactionResponse) => ({
        type: TableCellType.Text,
        value: row.cardNumber && FormatUtils.toSliceNumber(row.cardNumber),
      }),
      Date: (row: TransactionResponse) => ({
        type: TableCellType.Text,
        value: DateUtils.formatDate(row.date),
      }),
      Description: (row: TransactionResponse) => ({
        type: TableCellType.Text,
        value: row.description,
      }),
      Amount: (row: TransactionResponse) => ({
        type: TableCellType.Text,
        value: AmountUtils.transformToAmountWithCurrency(row.amount, row.currency),
      }),
      Fee: (row: TransactionResponse) => ({
        type: TableCellType.Text,
        value: typeof row.fee === 'number' ? AmountUtils.transformToAmountWithCurrency(row.fee, row.baseCurrency) : '-',
      }),
      TotalSpent: (row: TransactionResponse) => ({
        type: TableCellType.Text,
        value: AmountUtils.transformToAmountWithCurrency(row.totalAmount, row.baseCurrency),
      }),
      Category: (row: TransactionResponse) => ({
        type: TableCellType.Custom,
        value: !row.category ? (
          <AddInReports showModal={() => openShowCategoryModal(row)} />
        ) : (
          <div className='flex items-center gap-2' onClick={() => openShowCategoryModal(row)}>
            {row.category}
            <Icon icon={<EditIcon />} />
          </div>
        ),
      }),
      Receipt: (row: TransactionResponse) => ({
        type: TableCellType.Custom,
        value: !row.receipt ? (
          <AddInReports showModal={() => openShowReceiptModal(row)} />
        ) : (
          <ShowInReports showModal={() => openShowReceiptModal(row)} icon={<FileIcon />} />
        ),
      }),
      Note: (row: TransactionResponse) => ({
        type: TableCellType.Custom,
        value: !row.note ? <AddInReports showModal={() => openShowNoteModal(row)} /> : <ShowInReports showModal={() => openShowNoteModal(row)} />,
      }),
    }),
    [],
  );
  const items = useMemo(() => {
    return props.items.map((row) => {
      return props.columns.reduce((acc: { type: TableCellType; value: React.ReactNode }[], col) => {
        const cell = cells[col]?.(row);
        cell && acc.push(cell);
        return acc;
      }, []);
    });
  }, [props.items, props.columns]);

  return (
    <>
      <Table
        isLoading={props.isLoading}
        columns={props.columns.map((column) => t(column))}
        items={items}
        pagination={{
          currentPage: props.currentPage,
          totalItemCount: props.totalItemCount,
          countItemPage: props.countItemPage,
          onChangePage: props.onChangePage,
          onChangeCountItemPage: props.onChangeCountItemPage,
        }}
        isScrollable
      />
      {currentTransactionItem && (
        <>
          <AddReceiptModal
            show={showReceiptModal}
            onClose={() => setShowReceiptModal(false)}
            receipt={currentTransactionItem.receipt}
            refNum={currentTransactionItem.ref}
            removeReceipt={removeReceipt}
            onSave={async (file, isDeleted) => {
              if (!file) {
                setShowReceiptModal(false);
                isDeleted && props.onUpdate?.();
                return;
              }
              const formData = new FormData();
              formData.append('receipt', file);
              addReceipt(formData);
            }}
          />
          <AddNoteModal
            show={showNoteModal}
            onClose={() => setShowNoteModal(false)}
            onSave={updateNote}
            initialNote={currentTransactionItem.note || ''}
          />
          <AddCategoryModal
            show={showCategoryModal}
            onClose={() => setShowCategoryModal(false)}
            onSave={updateCategory}
            initialCategory={currentTransactionItem.category || ''}
          />
        </>
      )}
    </>
  );
};
